/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import axiosClient from '../../api/axiosClient';
import { widgetStyles, animations } from '../../utils/designTokens';
import { CircularProgress } from '@mui/material';
import PortalModal from '../../components/shared/PortalModal';
import ProfileAvatar from '../../components/shared/ProfileAvatar';
import ProfileImageUpload from '../../components/shared/ProfileImageUpload';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-3 md:p-4 lg:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
      <div className="h-8 w-8 md:h-10 md:w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center flex-shrink-0" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm md:text-base font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 hidden md:block">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

// Icons using SVG for consistency with client page style
const Icons = {
  Plus: () => (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  ),
  Search: () => (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  Edit: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  Deactivate: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  Activate: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  Eye: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ),
  Loading: () => (
    <svg className="animate-spin h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  Delete: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  )
};

// View Barber Modal
const ViewBarberModal = ({ isOpen, onClose, barber, isDark }) => {
  const { t } = useTranslation('admin');
  if (!barber) return null;

  return (
    <PortalModal open={isOpen && !!barber}>
      <div className="hc-modal-overlay bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center">
        <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="hc-modal-panel p-4 md:p-6 rounded-2xl max-w-md w-full my-8 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-2xl"
      >
        <div className="flex items-center mb-4">
          <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center mr-3" style={{ '--accent': ACCENT }}>
            <Icons.Eye />
          </div>
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {t('admin.barber_profile')}
          </h3>
        </div>

        <div className="flex items-center space-x-4 mb-6">
          <ProfileAvatar
            name={barber.name}
            avatarUrl={barber.avatar_url}
            size="xl"
            previewable
          />
          <div>
            <h4 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
              {barber.name}
            </h4>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              barber.status === 'active' 
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' 
                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
            }`}>
              {barber.status === 'active' ? t('admin.status_active') : t('admin.status_disabled')}
            </span>
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {t('admin.form_email')}
            </label>
            <p className="mt-1 text-zinc-900 dark:text-zinc-100">
              {barber.email}
            </p>
          </div>
          
          <div>
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {t('admin.form_phone')}
            </label>
            <p className="mt-1 text-zinc-900 dark:text-zinc-100">
              {barber.phone || <span className="text-zinc-500 dark:text-zinc-400">{t('admin.not_provided')}</span>}
            </p>
          </div>
          
          <div>
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {t('admin.last_login')}
            </label>
            <p className="mt-1 text-zinc-900 dark:text-zinc-100">
              {barber.last_login_at 
                ? new Date(barber.last_login_at).toLocaleString()
                : <span className="text-zinc-500 dark:text-zinc-400">{t('admin.never')}</span>
              }
            </p>
          </div>
          
          <div>
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {t('admin.created_at')}
            </label>
            <p className="mt-1 text-zinc-900 dark:text-zinc-100">
              {new Date(barber.created_at).toLocaleString()}
            </p>
          </div>
        </div>
        
        <div className="flex justify-end mt-6">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
          >
            {t('admin.close')}
          </motion.button>
        </div>
        </motion.div>
      </div>
    </PortalModal>
  );
};

// Edit Barber Modal
const EditBarberModal = ({ isOpen, onClose, barber, onSuccess, isDark, user, shopId }) => {
  const { t } = useTranslation('admin');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    avatar_url: '',
    status: 'active'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Initialize form data when barber changes
  useEffect(() => {
    if (barber) {
      setFormData({
        name: barber.name || '',
        email: barber.email || '',
        phone: barber.phone || '',
        avatar_url: barber.avatar_url || '',
        status: barber.status || 'active'
      });
    }
  }, [barber]);

  const resolvedShopId = (() => {
    const urlShopId =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('shopId')
        : null;
    return shopId || user?.shop_id || urlShopId || null;
  })();
  const canManageStatus = ['superadmin', 'shop_admin', 'admin'].includes(user?.role);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!resolvedShopId) {
        setError(t('admin.missing_shop_id'));
        return;
      }
      const response = await axiosClient.patch(`/admin/barbers/${barber.id}`, {
        ...formData,
        status: undefined,
        shop_id: resolvedShopId
      });

      if (response.data.ok) {
        let updatedBarber = response.data.data;
        if (canManageStatus && formData.status !== barber.status) {
          const statusResponse = await axiosClient.patch(
            `/admin/barbers/${barber.id}/status?shop_id=${resolvedShopId}`,
            { status: formData.status }
          );
          if (statusResponse.data?.ok) {
            updatedBarber = { ...updatedBarber, status: formData.status };
          }
        }
        onSuccess(updatedBarber);
        onClose();
      } else {
        setError(response.data.message || t('admin.update_barber'));
      }
    } catch (err) {
      console.error('Error updating barber:', err);
      setError(err.response?.data?.message || t('admin.update_barber'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!barber) return null;

  return (
    <PortalModal open={isOpen && !!barber}>
      <div className="hc-modal-overlay bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center">
        <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="hc-modal-panel p-4 md:p-6 rounded-2xl max-w-md w-full my-8 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-2xl"
      >
        <div className="flex items-center mb-4">
          <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center mr-3" style={{ '--accent': ACCENT }}>
            <Icons.Edit />
          </div>
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {t('admin.edit_barber')}
          </h3>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              {t('admin.form_name')} *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
              placeholder={t('admin.barber_full_name_placeholder')}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              {t('admin.form_email')} *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
              placeholder="barber@example.com"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              {t('admin.form_phone')}
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
              placeholder="+1234567890"
            />
          </div>

          {canManageStatus && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.col_status')}
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              >
                <option value="active">{t('admin.status_active')}</option>
                <option value="disabled">{t('admin.status_disabled')}</option>
              </select>
            </div>
          )}

          {/* Profile Image Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">
              Profile Photo
            </label>
            <div className="flex justify-center">
              <ProfileImageUpload
                name={formData.name}
                userId={barber?.id}
                shopId={resolvedShopId}
                currentAvatarUrl={formData.avatar_url}
                onUploadSuccess={(newUrl) =>
                  setFormData((prev) => ({ ...prev, avatar_url: newUrl }))
                }
                onRemoveSuccess={() =>
                  setFormData((prev) => ({ ...prev, avatar_url: '' }))
                }
                size="xl"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
            >
              {t('admin.cancel')}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-xl text-white font-medium flex items-center gap-2 transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: ACCENT }}
            >
              {loading ? <Icons.Loading /> : <Icons.Edit />}
              <span>{t('admin.update_barber')}</span>
            </motion.button>
          </div>
        </form>
        </motion.div>
      </div>
    </PortalModal>
  );
};

// Create/Invite Barber Modal
const CreateBarberModal = ({ isOpen, onClose, onSuccess, isDark, user }) => {
  const { t } = useTranslation('admin');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resolvedShopId = (() => {
    const urlShopId =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('shopId')
        : null;
    return user?.shop_id || urlShopId || null;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axiosClient.post('/admin/barbers', {
        ...formData,
        shop_id: resolvedShopId,
      });

      if (response.data.ok) {
        let createdBarber = response.data.data;

        // If there's a staged image file, upload it now that we have the userId
        if (pendingImageFile && createdBarber?.id && resolvedShopId) {
          try {
            const fd = new FormData();
            fd.append('image', pendingImageFile);
            const imgRes = await axiosClient.post(
              `/admin/profile-images/${createdBarber.id}?shop_id=${resolvedShopId}`,
              fd,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            if (imgRes.data?.ok) {
              createdBarber = { ...createdBarber, avatar_url: imgRes.data.data.avatar_url };
            }
          } catch (imgErr) {
            console.warn('[CreateBarber] profile image upload failed:', imgErr);
          }
        }

        onSuccess(createdBarber);
        onClose();
        setFormData({ name: '', email: '', phone: '' });
        setPendingImageFile(null);
        setPendingPreviewUrl(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('admin.create_barber'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleFileSelected = (file) => {
    setPendingImageFile(file);
    if (file) {
      const url = URL.createObjectURL(file);
      setPendingPreviewUrl(url);
    } else {
      setPendingPreviewUrl(null);
    }
  };

  if (!isOpen) return null;

  return (
    <PortalModal open={isOpen}>
      <div className="hc-modal-overlay bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center">
        <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="hc-modal-panel p-4 md:p-6 rounded-2xl max-w-md w-full my-8 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-2xl"
      >
        <div className="flex items-center mb-4">
          <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center mr-3" style={{ '--accent': ACCENT }}>
            <Icons.Plus />
          </div>
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {t('admin.create_barber')}
          </h3>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Profile photo (staged) */}
          <div className="mb-5 flex justify-center">
            <ProfileImageUpload
              name={formData.name}
              userId={null}
              shopId={resolvedShopId}
              currentAvatarUrl={pendingPreviewUrl}
              onFileSelected={handleFileSelected}
              onRemoveSuccess={() => handleFileSelected(null)}
              size="xl"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              {t('admin.form_name')} *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
              placeholder={t('admin.barber_full_name_placeholder')}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              {t('admin.form_email')} *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
              placeholder="barber@example.com"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              {t('admin.form_phone')}
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="+1234567890"
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
            />
          </div>

          <div className="flex justify-end space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
            >
              {t('admin.cancel')}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-xl text-white font-medium flex items-center gap-2 transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: ACCENT }}
            >
              {loading ? <Icons.Loading /> : <Icons.Plus />}
              <span>{t('admin.create_barber')}</span>
            </motion.button>
          </div>
        </form>
        </motion.div>
      </div>
    </PortalModal>
  );
};

// Barber Row Component
const BarberRow = ({ barber, onEdit, onStatusChange, onView, onDelete, isDark, userRole, isSelected, onSelect }) => {
  const [updating, setUpdating] = useState(false);

  const handleStatusChange = async () => {
    setUpdating(true);
    const newStatus = barber.status === 'active' ? 'disabled' : 'active';
    await onStatusChange(barber.id, newStatus);
    setUpdating(false);
  };

  const canEdit = ['superadmin', 'shop_admin', 'admin', 'barber'].includes(userRole);
  const canChangeStatus = ['superadmin', 'shop_admin', 'admin'].includes(userRole);

  return (
    <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
      <td className="p-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(barber.id)}
          className={`rounded border-2 ${
            isDark 
              ? 'bg-gray-700 border-gray-600 text-blue-500 focus:ring-blue-500' 
              : 'bg-white border-gray-300 text-blue-600 focus:ring-blue-500'
          }`}
          title={`Select ${barber.name}`}
        />
      </td>
      <td className="p-4">
        <div className="flex items-center space-x-3">
          <ProfileAvatar name={barber.name} avatarUrl={barber.avatar_url} size="sm" previewable />
          <div>
            <div className="font-medium">{barber.name}</div>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {barber.email}
            </div>
          </div>
        </div>
      </td>
      <td className="p-4">
        {barber.phone || <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>—</span>}
      </td>
      <td className="p-4">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          barber.status === 'active' 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {barber.status}
        </span>
      </td>
      <td className="p-4">
        {barber.last_login_at 
          ? new Date(barber.last_login_at).toLocaleDateString()
          : <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Never</span>
        }
      </td>
      <td className="p-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onView(barber)}
            className={`p-1 rounded hover:bg-opacity-20 ${
              isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
            }`}
            title="View Profile"
          >
            <Icons.Eye />
          </button>
          
          {canEdit && (
            <button
              onClick={() => onEdit(barber)}
              className={`p-1 rounded hover:bg-opacity-20 ${
                isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
              }`}
              title="Edit"
            >
              <Icons.Edit />
            </button>
          )}
          
          {canChangeStatus && (
            <button
              onClick={handleStatusChange}
              disabled={updating}
              className={`p-1 rounded hover:bg-opacity-20 ${
                isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
              }`}
              title={barber.status === 'active' ? 'Deactivate' : 'Activate'}
            >
              {updating ? (
                <Icons.Loading />
              ) : barber.status === 'active' ? (
                <Icons.Deactivate />
              ) : (
                <Icons.Activate />
              )}
            </button>
          )}
          
          {userRole === 'admin' && (
            <button
              onClick={() => onDelete(barber)}
              className={`p-1 rounded hover:bg-opacity-20 ${
                isDark ? 'hover:bg-red-600' : 'hover:bg-red-200'
              }`}
              title="Delete Barber"
            >
              <Icons.Delete />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// Main Barbers Component
const Barbers = () => {
  const { isDark } = useTheme();
  const { user } = useAdminAuth();
  const { t } = useTranslation('admin');
  const location = useLocation();
  
  // State
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });

  // Filters
  const [filters, setFilters] = useState({
    q: '',
    status: 'active'
  });

  // UI State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [selectedBarbers, setSelectedBarbers] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [focusBarberId, setFocusBarberId] = useState(null);

  // Load barbers
  const loadBarbers = useCallback(async (page = 1, newFilters = filters) => {
    try {
      setLoading(true);
      setError('');

      const urlShopId =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('shopId') : null;
      const resolvedShopId = user?.shop_id || urlShopId || null;
      if (!resolvedShopId) {
        setError(t('admin.missing_shop_id'));
        return;
      }
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pagination.pageSize.toString(),
        shop_id: resolvedShopId,
        ...newFilters
      });

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params.get(key) === '' || params.get(key) === null) {
          params.delete(key);
        }
      });

      const response = await axiosClient.get(`/admin/barbers?${params}`);
      
      if (response.data.ok) {
        setBarbers(response.data.data);
        setPagination(response.data.pagination);
      } else {
        throw new Error(response.data.error || t('admin.no_barbers'));
      }
    } catch (err) {
      console.error('Error loading barbers:', err);
      setError(err.response?.data?.error || err.message || t('admin.no_barbers'));
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.pageSize]);

  // Load barbers on mount and filter changes
  useEffect(() => {
    loadBarbers();
  }, [loadBarbers]);

  // Accept deep-links from global search: ?q=...&focus_barber_id=123
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';
    const focusId = params.get('focus_barber_id');

    if (q && q !== filters.q) {
      const newFilters = { ...filters, q };
      setFilters(newFilters);
      loadBarbers(1, newFilters);
    }
    if (focusId) {
      const n = Number(focusId);
      if (Number.isFinite(n)) setFocusBarberId(n);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // After barbers render, scroll & highlight the focused barber
  useEffect(() => {
    if (!focusBarberId) return;
    if (!barbers || barbers.length === 0) return;
    const row = document.getElementById(`barber-row-${focusBarberId}`);
    if (row) {
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const t = setTimeout(() => setFocusBarberId(null), 4000);
      return () => clearTimeout(t);
    }
  }, [focusBarberId, barbers]);

  // Handle search
  const handleSearch = (value) => {
    const newFilters = { ...filters, q: value };
    setFilters(newFilters);
    loadBarbers(1, newFilters);
  };

  // Handle status filter
  const handleStatusFilter = (status) => {
    const newFilters = { ...filters, status };
    setFilters(newFilters);
    loadBarbers(1, newFilters);
  };

  // Handle barber creation success
  const handleCreateSuccess = (newBarber) => {
    setBarbers(prev => [newBarber, ...prev]);
    // Reload to get accurate pagination
    loadBarbers();
  };

  // Handle status change
  const handleStatusChange = async (barberId, newStatus) => {
    try {
      const urlShopId =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('shopId') : null;
      const resolvedShopId = user?.shop_id || urlShopId || null;
      if (!resolvedShopId) throw new Error('Missing shop id');
      const response = await axiosClient.patch(`/admin/barbers/${barberId}/status?shop_id=${resolvedShopId}`, {
        status: newStatus
      });

      if (response.data.ok) {
        setBarbers(prev => prev.map(barber => 
          barber.id === barberId 
            ? { ...barber, status: newStatus }
            : barber
        ));
      }
    } catch (err) {
      console.error('Error updating barber status:', err);
      // TODO: Show error toast
    }
  };

  // Handle edit
  const handleEdit = (barber) => {
    setSelectedBarber(barber);
    setShowEditModal(true);
  };

  // Handle view
  const handleView = (barber) => {
    setSelectedBarber(barber);
    setShowViewModal(true);
  };

  // Handle edit success
  const handleEditSuccess = (updatedBarber) => {
    if (!updatedBarber || !updatedBarber.id) {
      console.error('Invalid updatedBarber data:', updatedBarber);
      return;
    }
    
    setBarbers(prev => prev.map(barber => 
      barber.id === updatedBarber.id ? updatedBarber : barber
    ));
    setShowEditModal(false);
    setSelectedBarber(null);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    loadBarbers(newPage);
  };

  const canCreateBarbers = ['superadmin', 'shop_admin', 'admin'].includes(user?.role);

  // Select all functionality
  const handleSelectAll = () => {
    if (selectedBarbers.size === barbers.length) {
      // Deselect all
      setSelectedBarbers(new Set());
    } else {
      // Select all visible barbers
      setSelectedBarbers(new Set(barbers.map(barber => barber.id)));
    }
  };

  const handleSelectBarber = (barberId) => {
    const newSelected = new Set(selectedBarbers);
    if (newSelected.has(barberId)) {
      newSelected.delete(barberId);
    } else {
      newSelected.add(barberId);
    }
    setSelectedBarbers(newSelected);
  };

  const isAllSelected = barbers.length > 0 && selectedBarbers.size === barbers.length;
  const isPartiallySelected = selectedBarbers.size > 0 && selectedBarbers.size < barbers.length;

  // Bulk actions
  const handleBulkActivate = async () => {
    if (selectedBarbers.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const urlShopId =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('shopId') : null;
      const resolvedShopId = user?.shop_id || urlShopId || null;
      if (!resolvedShopId) throw new Error('Missing shop id');
      const activatePromises = Array.from(selectedBarbers).map(barberId => 
        axiosClient.patch(`/admin/barbers/${barberId}/status?shop_id=${resolvedShopId}`, {
          status: 'active'
        })
      );
      
      await Promise.all(activatePromises);
      
      console.log(`✅ Successfully activated ${selectedBarbers.size} barber(s)`);
      setSelectedBarbers(new Set());
      loadBarbers();
    } catch (error) {
      console.error('Error activating barbers:', error);
      setError(`Failed to activate ${selectedBarbers.size} barber(s)`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedBarbers.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const urlShopId =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('shopId') : null;
      const resolvedShopId = user?.shop_id || urlShopId || null;
      if (!resolvedShopId) throw new Error('Missing shop id');
      const deactivatePromises = Array.from(selectedBarbers).map(barberId => 
        axiosClient.patch(`/admin/barbers/${barberId}/status?shop_id=${resolvedShopId}`, {
          status: 'disabled'
        })
      );
      
      await Promise.all(deactivatePromises);
      
      console.log(`✅ Successfully deactivated ${selectedBarbers.size} barber(s)`);
      setSelectedBarbers(new Set());
      loadBarbers();
    } catch (error) {
      console.error('Error deactivating barbers:', error);
      setError(`Failed to deactivate ${selectedBarbers.size} barber(s)`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedBarbers.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const urlShopId =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('shopId') : null;
      const resolvedShopId = user?.shop_id || urlShopId || null;
      if (!resolvedShopId) throw new Error('Missing shop id');
      const deletePromises = Array.from(selectedBarbers).map(barberId => 
        axiosClient.patch(`/admin/barbers/${barberId}/status?shop_id=${resolvedShopId}`, {
          status: 'deleted'
        })
      );
      
      await Promise.all(deletePromises);
      
      console.log(`✅ Successfully deleted ${selectedBarbers.size} barber(s)`);
      setSelectedBarbers(new Set());
      loadBarbers();
    } catch (error) {
      console.error('Error deleting barbers:', error);
      setError(`Failed to delete ${selectedBarbers.size} barber(s)`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Clear selection when filters change
  useEffect(() => {
    setSelectedBarbers(new Set());
  }, [filters.q, filters.status, pagination.page]);

  // Individual delete handler
  const handleDelete = (barber) => {
    setConfirmData({
      title: t('admin.delete') + ' ' + t('admin.role_barber'),
      message: t('admin.delete_barber_confirm', { name: barber.name }),
      details: [
        `${t('admin.form_name')}: ${barber.name}`,
        `${t('admin.form_email')}: ${barber.email}`,
        `${t('admin.col_status')}: ${barber.status === 'active' ? t('admin.status_active') : t('admin.status_disabled')}`
      ],
      warning: t('admin.delete_barber_warning'),
      onConfirm: () => performDeleteBarber(barber)
    });
    setShowConfirmModal(true);
  };

  const performDeleteBarber = async (barber) => {
    try {
      const urlShopId =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('shopId') : null;
      const resolvedShopId = user?.shop_id || urlShopId || null;
      if (!resolvedShopId) throw new Error('Missing shop id');
      await axiosClient.patch(`/admin/barbers/${barber.id}/status?shop_id=${resolvedShopId}`, {
        status: 'deleted'
      });
      console.log(`✅ Successfully deleted barber: ${barber.name}`);
      loadBarbers();
    } catch (error) {
      console.error('Error deleting barber:', error);
      setError(`Failed to delete ${barber.name}`);
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
              <svg className="h-5 w-5 md:h-6 md:w-6 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {t('admin.barbers_page_title')}
            </h1>
            <p className="text-xs md:text-sm text-zinc-600 dark:text-zinc-400 mt-0.5 md:mt-1">
              {t('admin.barbers_subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            {canCreateBarbers && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCreateModal(true)}
                className="px-3 md:px-4 py-2 rounded-xl text-white text-sm md:text-base font-medium flex items-center gap-1.5 md:gap-2 transition-all duration-200 hover:shadow-lg whitespace-nowrap"
                style={{ background: ACCENT }}
              >
                <Icons.Plus />
                <span className="hidden xs:inline">{t('admin.create_barber')}</span>
                <span className="xs:hidden">{t('admin.create')}</span>
              </motion.button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4 md:mb-6">
        <CardHeader
          title={t('admin.filters')}
          subtitle={t('admin.filter_barbers_subtitle')}
          icon={
            <svg className="h-4 w-4 md:h-5 md:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
            </svg>
          }
        />
        <div className="p-3 md:p-4 lg:p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            <div>
              <label className="block text-xs md:text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5 md:mb-2">
                {t('admin.col_status')}
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleStatusFilter(e.target.value)}
                className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              >
                <option value="all">{t('admin.all_barbers_filter')}</option>
                <option value="active">{t('admin.status_active')}</option>
                <option value="disabled">{t('admin.status_disabled')}</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs md:text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5 md:mb-2">
                {t('admin.search_barber')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2 md:pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 md:h-5 md:w-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={filters.q}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder={t('admin.search_name_email_phone')}
                  className="w-full pl-8 md:pl-10 pr-3 md:pr-4 py-2 md:py-3 text-sm md:text-base rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                  style={{ '--accent': ACCENT }}
                />
              </div>
            </div>
          </div>
          
          {/* Bulk Actions */}
          {selectedBarbers.size > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10 dark:border-white/10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-zinc-600 dark:text-zinc-400">
                  {t('admin.selected_count', { count: selectedBarbers.size })}
                </span>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBulkActivate}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-sm rounded-lg flex items-center text-white disabled:opacity-50 transition-all"
                  style={{ backgroundColor: ACCENT }}
                >
                  {bulkActionLoading ? <Icons.Loading /> : <Icons.Activate />}
                  <span className="ml-1">{t('admin.activate')}</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBulkDeactivate}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-sm rounded-lg flex items-center bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 transition-all"
                >
                  {bulkActionLoading ? <Icons.Loading /> : <Icons.Deactivate />}
                  <span className="ml-1">{t('admin.deactivate')}</span>
                </motion.button>
                {user?.role === 'shop_admin' && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-sm rounded-lg flex items-center bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 transition-all"
                  >
                    {bulkActionLoading ? <Icons.Loading /> : <Icons.Delete />}
                    <span className="ml-1">{t('admin.delete')}</span>
                  </motion.button>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Barbers Table */}
      <Card>
        <CardHeader
          title={t('admin.barbers_page_title')}
          subtitle={t('admin.total_barbers', { count: pagination.total })}
          icon={
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          }
        />
        <div className="admin-table-container admin-table-container--mobile-scroll">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 dark:text-zinc-400 border-b border-white/10 dark:border-white/10">
                {/* Mobile-only columns (use horizontal swipe-scroll) */}
                <th className="py-4 px-4 font-medium md:hidden">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = isPartiallySelected;
                    }}
                    onChange={handleSelectAll}
                    className="rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                    title={isAllSelected ? 'Deselect all' : 'Select all'}
                  />
                </th>
                <th className="py-4 px-4 font-medium hidden md:table-cell">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = isPartiallySelected;
                    }}
                    onChange={handleSelectAll}
                    className="rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                    title={isAllSelected ? 'Deselect all' : 'Select all'}
                  />
                </th>
                <th className="py-4 px-4 font-medium">{t('admin.col_barber')}</th>
                {/* Mobile-only columns (use horizontal swipe-scroll) */}
                <th className="py-4 px-4 font-medium md:hidden">{t('admin.col_phone')}</th>
                <th className="py-4 px-4 font-medium hidden lg:table-cell">{t('admin.col_phone')}</th>
                <th className="py-4 px-4 font-medium">{t('admin.col_status')}</th>
                {/* Mobile-only columns (use horizontal swipe-scroll) */}
                <th className="py-4 px-4 font-medium md:hidden">{t('admin.col_last_login')}</th>
                <th className="py-4 px-4 font-medium hidden xl:table-cell">{t('admin.col_last_login')}</th>
                <th className="py-4 px-4 font-medium text-right">{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 dark:divide-white/10">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center">
                    <div className="flex items-center justify-center gap-3 text-zinc-600 dark:text-zinc-400">
                      <CircularProgress size={24} style={{ color: ACCENT }} />
                      <span>{t('admin.loading_barbers')}</span>
                    </div>
                  </td>
                </tr>
              ) : barbers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center">
                    <div className="text-center">
                      <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                        <svg className="h-6 w-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                        {t('admin.no_barbers')}
                      </h3>
                      <p className="text-zinc-500 dark:text-zinc-400">
                        {canCreateBarbers ? t('admin.create_first_barber') : t('admin.no_barbers_match_filters')}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                barbers.map((barber) => (
                  <motion.tr 
                    key={barber.id} 
                    id={`barber-row-${barber.id}`}
                    className={`text-zinc-800 dark:text-zinc-100 hover:bg-white/30 dark:hover:bg-zinc-800/30 transition-colors ${
                      focusBarberId === barber.id ? 'ring-2 ring-[#7d6633] ring-offset-2 ring-offset-white dark:ring-offset-zinc-900' : ''
                    }`}
                    whileHover={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                  >
                    {/* Mobile-only selection */}
                    <td className="py-4 px-4 md:hidden">
                      <input
                        type="checkbox"
                        checked={selectedBarbers.has(barber.id)}
                        onChange={() => handleSelectBarber(barber.id)}
                        className="rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                        title={`Select ${barber.name}`}
                      />
                    </td>
                    <td className="py-4 px-4 hidden md:table-cell">
                      <input
                        type="checkbox"
                        checked={selectedBarbers.has(barber.id)}
                        onChange={() => handleSelectBarber(barber.id)}
                        className="rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                        title={`Select ${barber.name}`}
                      />
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        <ProfileAvatar
                          name={barber.name}
                          avatarUrl={barber.avatar_url}
                          size="sm"
                          previewable
                        />
                        <div className="ml-3 min-w-0 flex-1">
                          <div className="font-medium text-zinc-900 dark:text-zinc-100">
                            {barber.name}
                          </div>
                          <div className="text-sm text-zinc-500 dark:text-zinc-400">
                            {barber.email}
                          </div>
                          {/* Tablet: Show phone stacked (keeps tablet UX unchanged) */}
                          {barber.phone && (
                            <div className="hidden md:block lg:hidden mt-1">
                              <div className="text-xs text-zinc-500 dark:text-zinc-400">{barber.phone}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    {/* Mobile-only columns (use horizontal swipe-scroll) */}
                    <td className="py-4 px-4 md:hidden">
                      <div className="text-zinc-800 dark:text-zinc-100">
                        {barber.phone || <span className="text-zinc-500 dark:text-zinc-400">—</span>}
                      </div>
                    </td>
                    <td className="py-4 px-4 hidden lg:table-cell">
                      <div className="text-zinc-800 dark:text-zinc-100">
                        {barber.phone || <span className="text-zinc-500 dark:text-zinc-400">—</span>}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        barber.status === 'active' 
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' 
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        {barber.status === 'active' ? t('admin.status_active') : t('admin.status_disabled')}
                      </span>
                    </td>
                    {/* Mobile-only columns (use horizontal swipe-scroll) */}
                    <td className="py-4 px-4 md:hidden">
                      <div className="text-zinc-800 dark:text-zinc-100">
                        {barber.last_login_at 
                          ? new Date(barber.last_login_at).toLocaleDateString()
                          : <span className="text-zinc-500 dark:text-zinc-400">{t('admin.never')}</span>
                        }
                      </div>
                    </td>
                    <td className="py-4 px-4 hidden xl:table-cell">
                      <div className="text-zinc-800 dark:text-zinc-100">
                        {barber.last_login_at 
                          ? new Date(barber.last_login_at).toLocaleDateString()
                          : <span className="text-zinc-500 dark:text-zinc-400">{t('admin.never')}</span>
                        }
                      </div>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleView(barber)}
                          className="p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                          title={t('admin.view_profile')}
                        >
                          <Icons.Eye />
                        </motion.button>
                        
                        {(['superadmin', 'shop_admin', 'admin'].includes(user?.role) ||
                          (user?.role === 'barber' && Number(barber.id) === Number(user?.id))) && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleEdit(barber)}
                            className="p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                            title={t('admin.edit')}
                          >
                            <Icons.Edit />
                          </motion.button>
                        )}
                        
                        {['superadmin', 'shop_admin', 'admin'].includes(user?.role) && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleStatusChange(barber.id, barber.status === 'active' ? 'disabled' : 'active')}
                            className="p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                            title={barber.status === 'active' ? t('admin.deactivate') : t('admin.activate')}
                          >
                            {barber.status === 'active' ? <Icons.Deactivate /> : <Icons.Activate />}
                          </motion.button>
                        )}
                        
                        {user?.role === 'shop_admin' && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDelete(barber)}
                            className="p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                            title={t('admin.delete') + ' ' + t('admin.role_barber')}
                          >
                            <Icons.Delete />
                          </motion.button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Card className="mt-6">
          <div className="p-4 md:p-5">
            <div className="flex items-center justify-between admin-pagination-wrapper">
              <div className="text-sm text-zinc-600 dark:text-zinc-400 admin-pagination-info">
                {t('admin.showing_results_range', { from: ((pagination.page - 1) * pagination.pageSize) + 1, to: Math.min(pagination.page * pagination.pageSize, pagination.total), total: pagination.total, entity: t('admin.barbers_page_title').toLowerCase() })}
              </div>
              <div className="flex items-center gap-4 admin-pagination-controls">
                <div className="flex items-center gap-2 admin-pagination-buttons">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {t('admin.previous')}
                  </motion.button>
                  <span className="px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400">
                    {t('admin.page_of', { page: pagination.page, total: pagination.totalPages })}
                  </span>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                    className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {t('admin.next')}
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Create Barber Modal */}
      <CreateBarberModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
        isDark={isDark}
        user={user}
      />

      {/* View Barber Modal */}
      <ViewBarberModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedBarber(null);
        }}
        barber={selectedBarber}
        isDark={isDark}
      />

      {/* Edit Barber Modal */}
      <EditBarberModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedBarber(null);
        }}
        barber={selectedBarber}
        onSuccess={handleEditSuccess}
        isDark={isDark}
        user={user}
        shopId={user?.shop_id || new URLSearchParams(window.location.search).get('shopId') || null}
      />

      {/* Confirmation Modal */}
      {showConfirmModal && confirmData && (
        <PortalModal open={showConfirmModal}>
          <div className="hc-modal-overlay bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center">
            <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="hc-modal-panel p-4 md:p-6 rounded-2xl max-w-md w-full my-8 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-2xl"
          >
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-3">
                <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                {confirmData.title}
              </h3>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-3">
                {confirmData.message}
              </p>
              {confirmData.details && (
                <div className="bg-zinc-100 dark:bg-zinc-800 p-3 rounded-xl text-sm">
                  {confirmData.details.map((detail, index) => (
                    <div key={index} className="text-zinc-700 dark:text-zinc-300">{detail}</div>
                  ))}
                </div>
              )}
              {confirmData.warning && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-3 font-medium">
                  {confirmData.warning}
                </p>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
              >
                {t('admin.cancel')}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setShowConfirmModal(false);
                  confirmData.onConfirm();
                }}
                className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium transition-all duration-200 hover:shadow-lg"
              >
                {t('admin.confirm')}
              </motion.button>
            </div>
            </motion.div>
          </div>
        </PortalModal>
      )}
    </div>
  );
};

export default Barbers;
